import { request, requestNoContent } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/** Cấp của một nút trong cây khách hàng. */
export const CustomerLevel = {
  /** Tập đoàn — nút gốc, gom nhiều pháp nhân của cùng một khách hàng. */
  Group: 1,

  /** Pháp nhân — cấp xuất hóa đơn. Bắt buộc có mã số thuế và địa chỉ hóa đơn. */
  LegalEntity: 2,

  /** Nhà máy hoặc địa điểm — cấp giao hàng. */
  Site: 3,

  /** Line sản xuất hoặc khu vực — cấp lắp đặt hệ thống. */
  Line: 4,
} as const;

/** Trạng thái quan hệ với khách hàng. */
export const CustomerStatus = {
  /** Tiềm năng — chưa phát sinh giao dịch. */
  Prospect: 1,

  /** Đang hoạt động. */
  Active: 2,

  /** Tạm ngừng — thường vì công nợ quá hạn. */
  Suspended: 3,

  /** Đã dừng hợp tác. */
  Terminated: 4,

  /** Danh sách đen — không giao dịch trong mọi trường hợp. */
  Blacklisted: 5,
} as const;

/** Ngành sản xuất của khách hàng. */
export const CustomerIndustry = {
  Electronics: 1,
  PrecisionMechanics: 2,
  Automotive: 3,
  PharmaceuticalAndMedicalDevice: 4,
  FoodAndBeverage: 5,
  Packaging: 6,
  Textile: 7,
  Other: 8,
} as const;

/** Nhóm khách hàng theo hình thức doanh nghiệp. */
export const CustomerGroup = {
  Multinational: 1,
  ForeignInvested: 2,
  Domestic: 3,
  IntegratorOrReseller: 4,
} as const;

/** Thông tin rút gọn của khách hàng, dùng cho danh sách và ô chọn. */
export interface CustomerListItem {
  readonly id: string;
  readonly code: string;
  readonly level: number;
  readonly shortName: string;
  readonly legalName: string;
  readonly taxCode: string | null;
  readonly parentId: string | null;
  readonly depth: number;
  readonly status: number;
  readonly isOpenForBusiness: boolean;
  readonly assignedSalesUserId: string | null;
}

/** Thông tin đầy đủ của một khách hàng. */
export interface CustomerDetail {
  readonly id: string;
  readonly code: string;
  readonly level: number;
  readonly parentId: string | null;
  readonly path: string;
  readonly depth: number;
  readonly sortOrder: number;
  readonly legalName: string;
  readonly shortName: string;
  readonly nameEn: string | null;
  readonly taxCode: string | null;
  readonly countryId: string;
  readonly invoiceAddress: string | null;
  readonly deliveryAddress: string | null;
  readonly deliveryNote: string | null;
  readonly phoneNumber: string | null;
  readonly email: string | null;
  readonly website: string | null;
  readonly industry: number | null;
  readonly group: number | null;
  readonly paymentTermDays: number | null;
  readonly creditLimitAmount: number | null;
  readonly defaultCurrencyCode: string | null;
  readonly defaultValueAddedTaxRateId: string | null;
  readonly defaultRetentionPercent: number | null;
  readonly assignedSalesUserId: string | null;
  readonly nonDisclosureDocumentId: string | null;
  readonly nonDisclosureExpiresOn: string | null;
  readonly confidentialityNote: string | null;
  readonly status: number;
  readonly statusReason: string | null;
  readonly firstProjectDate: string | null;
  readonly note: string | null;
  readonly isOpenForBusiness: boolean;

  /** Cấp này bắt buộc có mã số thuế và địa chỉ hóa đơn. Máy chủ tính sẵn. */
  readonly requiresTaxCode: boolean;

  /**
   * Thỏa thuận bảo mật sắp hết hiệu lực trong sáu mươi ngày tới.
   *
   * Máy chủ tính sẵn vì nó phụ thuộc ngày nghiệp vụ theo múi giờ công ty, mà máy của người dùng có
   * thể ở múi giờ khác hoặc đặt sai ngày.
   */
  readonly nonDisclosureExpiresWithinSixtyDays: boolean;
}

/** Điều kiện lọc danh sách khách hàng. */
export interface CustomerFilter {
  readonly searchTerm?: string;
  readonly level?: number;
  readonly status?: number;
  readonly parentId?: string;
  readonly assignedSalesUserId?: string;
  readonly onlyOpenForBusiness?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung gửi lên khi khai khách hàng mới. */
export interface CreateCustomerPayload {
  readonly level: number;
  readonly code: string;
  readonly legalName: string;
  readonly shortName: string;
  readonly nameEn: string | null;
  readonly taxCode: string | null;
  readonly countryId: string;
  readonly invoiceAddress: string | null;
  readonly parentId: string | null;
  readonly sortOrder: number;
}

/** Nội dung gửi lên khi sửa tên. */
export interface UpdateCustomerNamesPayload {
  readonly legalName: string;
  readonly shortName: string;
  readonly nameEn: string | null;
}

/** Nội dung gửi lên khi sửa thông tin in trên hóa đơn. */
export interface UpdateCustomerInvoiceIdentityPayload {
  readonly taxCode: string | null;
  readonly invoiceAddress: string | null;
  readonly countryId: string;
}

/** Nội dung gửi lên khi sửa thông tin giao hàng, liên lạc và phân loại. */
export interface UpdateCustomerProfilePayload {
  readonly deliveryAddress: string | null;
  readonly deliveryNote: string | null;
  readonly phoneNumber: string | null;
  readonly email: string | null;
  readonly website: string | null;
  readonly industry: number | null;
  readonly group: number | null;
  readonly note: string | null;
}

/** Nội dung gửi lên khi sửa điều khoản thương mại. */
export interface UpdateCustomerCommercialTermsPayload {
  readonly paymentTermDays: number | null;
  readonly creditLimitAmount: number | null;
  readonly defaultCurrencyCode: string | null;
  readonly defaultValueAddedTaxRateId: string | null;
  readonly defaultRetentionPercent: number | null;
}

/** Nội dung gửi lên khi sửa thông tin bảo mật. */
export interface UpdateCustomerConfidentialityPayload {
  readonly nonDisclosureDocumentId: string | null;
  readonly nonDisclosureExpiresOn: string | null;
  readonly confidentialityNote: string | null;
}

const baseRoute = '/api/v1/sales/customers';

/** Dựng phần truy vấn của địa chỉ, bỏ hẳn tham số rỗng. */
function buildQueryString(filter: CustomerFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.level !== undefined) {
    parameters.set('level', String(filter.level));
  }

  if (filter.status !== undefined) {
    parameters.set('status', String(filter.status));
  }

  if (filter.parentId !== undefined) {
    parameters.set('parentId', filter.parentId);
  }

  if (filter.assignedSalesUserId !== undefined) {
    parameters.set('assignedSalesUserId', filter.assignedSalesUserId);
  }

  if (filter.onlyOpenForBusiness === true) {
    parameters.set('onlyOpenForBusiness', 'true');
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', String(filter.pageNumber));
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', String(filter.pageSize));
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách khách hàng. */
export async function listCustomers(
  filter: CustomerFilter,
): Promise<PagedResponse<CustomerListItem>> {
  return await request<PagedResponse<CustomerListItem>>(
    `${baseRoute}${buildQueryString(filter)}`,
  );
}

/** Lấy chi tiết một khách hàng. */
export async function getCustomer(id: string): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}`);
}

/** Khai một khách hàng mới. */
export async function createCustomer(
  payload: CreateCustomerPayload,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(baseRoute, { method: 'POST', body: payload });
}

/** Xóa mềm một khách hàng. */
export async function deleteCustomer(id: string): Promise<void> {
  await requestNoContent(`${baseRoute}/${id}`, { method: 'DELETE' });
}

/** Sửa tên pháp lý, tên gọi tắt và tên tiếng Anh. */
/**
 * Đổi mã khách hàng.
 *
 * Quyền riêng, tách khỏi phép sửa tên: mã đã xuất hiện trên báo giá và chứng từ đã gửi đi. Đổi mã
 * KHÔNG làm đứt dữ liệu — mọi nơi khác trỏ tới khách hàng bằng định danh.
 */
export async function changeCustomerCode(id: string, code: string): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/code`, {
    method: 'PUT',
    body: { code },
  });
}

export async function updateCustomerNames(
  id: string,
  payload: UpdateCustomerNamesPayload,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/names`, {
    method: 'PUT',
    body: payload,
  });
}

/** Sửa thông tin giao hàng, liên lạc, phân loại và ghi chú. */
export async function updateCustomerProfile(
  id: string,
  payload: UpdateCustomerProfilePayload,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/profile`, {
    method: 'PUT',
    body: payload,
  });
}

/** Sửa mã số thuế, địa chỉ hóa đơn và quốc gia. */
export async function updateCustomerInvoiceIdentity(
  id: string,
  payload: UpdateCustomerInvoiceIdentityPayload,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/invoice-identity`, {
    method: 'PUT',
    body: payload,
  });
}

/** Sửa hạn mức công nợ, số ngày thanh toán và giá trị mặc định khi lập chứng từ. */
export async function updateCustomerCommercialTerms(
  id: string,
  payload: UpdateCustomerCommercialTermsPayload,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/commercial-terms`, {
    method: 'PUT',
    body: payload,
  });
}

/** Chỉ định người phụ trách khách hàng. */
export async function assignCustomerOwner(
  id: string,
  assignedSalesUserId: string | null,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/owner`, {
    method: 'PUT',
    body: { assignedSalesUserId },
  });
}

/** Sửa thỏa thuận bảo mật và lưu ý bảo mật riêng. */
export async function updateCustomerConfidentiality(
  id: string,
  payload: UpdateCustomerConfidentialityPayload,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/confidentiality`, {
    method: 'PUT',
    body: payload,
  });
}

/** Đổi trạng thái quan hệ với khách hàng. */
export async function changeCustomerStatus(
  id: string,
  status: number,
  reason: string | null,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/status`, {
    method: 'PUT',
    body: { status, reason },
  });
}

/** Di chuyển khách hàng cùng toàn bộ nhánh sang nút cha mới. */
export async function moveCustomer(
  id: string,
  newParentId: string | null,
  sortOrder: number,
): Promise<CustomerDetail> {
  return await request<CustomerDetail>(`${baseRoute}/${id}/parent`, {
    method: 'PUT',
    body: { newParentId, sortOrder },
  });
}
